import { useState } from 'react';
import { Alert, Button, Input, Modal, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/lib/table';

import useLocale from '../../../hooks/useLocale';
import type { ClientService } from '../../../shared/api/ClientService';
import { EMPTY_VALUE, MAX_REPLAY_IDS, REPLAY_MODAL_WIDTH, REPLAY_TEXT_ROWS } from './constants';
import { useReplayEvents } from './useIntegrationMonitoring';
import {
  canReplay,
  formatDateTime,
  formatInvalidTokens,
  formatText,
  getErrorMessage,
  getReplayItemPresentation,
  parseEventIds,
  summariseReplayResult,
} from './utils';

interface IProps {
  initialIds: string[];
  isIntegrationEnabled?: boolean;
  onReplayed?: () => void;
  onClose: () => void;
}

interface IResultProps {
  result: ClientService.ReplayEventsResultDto;
}

const ReplayResult = ({ result }: IResultProps): JSX.Element => {
  const { t } = useLocale();
  const summary = summariseReplayResult(result);

  const columns: ColumnsType<ClientService.ReplayEventItemDto> = [
    {
      title: t.INTEGRATION_EVENT_ID,
      dataIndex: 'eventId',
      key: 'eventId',
      render: (eventId?: string) =>
        eventId ? (
          <Typography.Text code copyable>
            {eventId}
          </Typography.Text>
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      title: t.STATUS,
      dataIndex: 'status',
      key: 'status',
      render: (status?: string) => {
        const presentation = getReplayItemPresentation(status);
        return <Tag color={presentation?.color}>{presentation ? t[presentation.labelKey] : status || EMPTY_VALUE}</Tag>;
      },
    },
    {
      title: t.DETAILS,
      key: 'details',
      render: (_: unknown, item: ClientService.ReplayEventItemDto) => {
        const presentation = getReplayItemPresentation(item.status);
        return presentation ? t[presentation.hintKey] : EMPTY_VALUE;
      },
    },
    { title: t.TYPE, dataIndex: 'type', key: 'type', render: (type?: string) => type || EMPTY_VALUE },
    {
      title: t.INTEGRATION_OCCURRED,
      dataIndex: 'occurredTime',
      key: 'occurredTime',
      render: (occurredTime?: ClientService.ReplayEventItemDto['occurredTime']) => formatDateTime(occurredTime),
    },
  ];

  return (
    <>
      <Typography.Paragraph strong>
        {formatText(t.INTEGRATION_REPLAY_RESULT_SUMMARY, summary.accepted, summary.requested)}
      </Typography.Paragraph>
      {(summary.notFound > 0 || summary.expired > 0) && (
        <Typography.Paragraph type="secondary">
          {formatText(t.INTEGRATION_REPLAY_RESULT_DETAILS, summary.notFound, summary.expired)}
        </Typography.Paragraph>
      )}
      {summary.accepted > 0 && (
        <Alert className="integration-monitoring__alert" type="info" showIcon message={t.INTEGRATION_REPLAY_HINT} />
      )}
      <Table size="small" rowKey="eventId" columns={columns} dataSource={result.items ?? []} pagination={false} />
    </>
  );
};

const ReplayEventsModal = ({ initialIds, isIntegrationEnabled, onReplayed, onClose }: IProps): JSX.Element => {
  const { t } = useLocale();
  const [text, setText] = useState(() => initialIds.join('\n'));
  const [result, setResult] = useState<ClientService.ReplayEventsResultDto>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const replayMutation = useReplayEvents();

  const parse = parseEventIds(text);
  const distinctCount = parse.validIds.length;
  const isReplaying = replayMutation.isLoading;

  const replay = async () => {
    setErrorMessage(undefined);
    try {
      setResult(await replayMutation.mutateAsync(parse.validIds));
      onReplayed?.();
    } catch (error) {
      setErrorMessage(getErrorMessage(error, t.INTEGRATION_ACTION_FAILED));
    }
  };

  const footer = result ? (
    <>
      <Button onClick={() => setResult(undefined)}>{t.INTEGRATION_REPLAY_BACK}</Button>
      <Button type="primary" onClick={onClose}>
        {t.CLOSE}
      </Button>
    </>
  ) : (
    <>
      <Button disabled={isReplaying} onClick={onClose}>
        {t.CLOSE}
      </Button>
      <Button type="primary" disabled={!canReplay(parse, isIntegrationEnabled)} loading={isReplaying} onClick={replay}>
        {formatText(t.INTEGRATION_REPLAY_SUBMIT, distinctCount)}
      </Button>
    </>
  );

  return (
    <Modal
      open
      title={t.INTEGRATION_REPLAY_BY_ID}
      width={REPLAY_MODAL_WIDTH}
      maskClosable={false}
      // While the call is in flight nothing can close the modal: the result would be lost.
      keyboard={!isReplaying}
      closable={!isReplaying}
      onCancel={onClose}
      footer={footer}
    >
      {result ? (
        <ReplayResult result={result} />
      ) : (
        <>
          {isIntegrationEnabled === false && (
            <Alert
              className="integration-monitoring__alert"
              type="warning"
              showIcon
              message={t.INTEGRATION_REPLAY_OFF_WARNING}
            />
          )}
          {isIntegrationEnabled === undefined && (
            <Alert
              className="integration-monitoring__alert"
              type="info"
              showIcon
              message={t.INTEGRATION_REPLAY_STATE_UNKNOWN}
            />
          )}
          <Typography.Paragraph type="secondary">{t.INTEGRATION_REPLAY_HELP}</Typography.Paragraph>
          <Input.TextArea
            aria-label={t.INTEGRATION_REPLAY_BY_ID}
            rows={REPLAY_TEXT_ROWS}
            placeholder={t.INTEGRATION_REPLAY_PLACEHOLDER}
            value={text}
            disabled={replayMutation.isLoading}
            onChange={({ target }) => setText(target.value)}
          />
          {text.trim() && (
            <div className="integration-monitoring__replay-feedback" role="status">
              <Typography.Text>{formatText(t.INTEGRATION_REPLAY_DISTINCT, distinctCount)}</Typography.Text>
              {parse.duplicateCount > 0 && (
                <Typography.Text type="secondary">
                  {formatText(t.INTEGRATION_REPLAY_DUPLICATES, parse.duplicateCount)}
                </Typography.Text>
              )}
              {parse.invalidTokens.length > 0 && (
                <Typography.Text type="danger">
                  {formatText(
                    t.INTEGRATION_REPLAY_INVALID,
                    parse.invalidTokens.length,
                    formatInvalidTokens(parse.invalidTokens)
                  )}
                </Typography.Text>
              )}
              {distinctCount > MAX_REPLAY_IDS && (
                <Typography.Text type="danger">
                  {formatText(t.INTEGRATION_REPLAY_TOO_MANY, MAX_REPLAY_IDS)}
                </Typography.Text>
              )}
            </div>
          )}
          {errorMessage && (
            <Alert className="integration-monitoring__replay-error" type="error" showIcon message={errorMessage} />
          )}
        </>
      )}
    </Modal>
  );
};

export default ReplayEventsModal;
